import { logger } from '@/lib/logger';
import React, { useState, useRef, useEffect } from 'react';
import { updateProfile } from 'firebase/auth';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';

export default function ProfileSettings({ user, userRole, userDepartment, setMessage }) {
    const { updateUserDataLocal } = useAuth();
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [jobTitle, setJobTitle] = useState('');
    const [avatarPreview, setAvatarPreview] = useState(user?.photoURL || null);
    const [avatarFile, setAvatarFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(false);
    
    const fileRef = useRef(null);

    // Load existing profile data from Firestore on mount
    useEffect(() => {
        if (!user?.uid) return;

        // Initial split from displayName if available
        if (user.displayName) {
            const parts = user.displayName.split(' ');
            if (parts.length > 0) setFirstName(parts[0]);
            if (parts.length > 1) setLastName(parts.slice(1).join(' '));
        }

        getDoc(doc(db, 'app_users', user.uid)).then(snap => {
            if (snap.exists()) {
                const d = snap.data();
                if (d.firstName) setFirstName(d.firstName);
                if (d.lastName) setLastName(d.lastName);
                if (d.phone) setPhone(d.phone);
                if (d.jobTitle) setJobTitle(d.jobTitle);
                if (d.photoURL && !avatarPreview) setAvatarPreview(d.photoURL);
            }
        }).catch(() => { });
    }, [user?.uid]);

    const initials = (firstName || lastName || user?.displayName || user?.email || 'U')
        .split(' ').map(w => w?.[0] || '').join('').toUpperCase().slice(0, 2);

    const handleAvatarChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 3 * 1024 * 1024) {
            setMessage({ type: 'error', text: 'Image must be under 3 MB' });
            return;
        }
        setAvatarFile(file);
        const reader = new FileReader();
        reader.onload = (ev) => setAvatarPreview(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let photoURL = user?.photoURL || null;
            // Upload to Cloudinary securely if there's a new file
            if (avatarFile) {
                const { auth } = await import('@/lib/firebase');
                const token = await auth.currentUser.getIdToken();
                const signReq = await fetch('/api/cloudinary-sign', {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!signReq.ok) throw new Error('Failed to securely sign upload request');
                const { signature, timestamp, preset } = await signReq.json();

                const formData = new FormData();
                formData.append('file', avatarFile);
                formData.append('upload_preset', preset || 'oblique_uploads');
                formData.append('timestamp', timestamp);
                formData.append('signature', signature);
                formData.append('api_key', import.meta.env.VITE_CLOUDINARY_API_KEY);

                const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
                const res = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
                    method: 'POST',
                    body: formData
                });

                if (!res.ok) throw new Error('Failed to upload securely to Cloudinary');

                const data = await res.json();
                photoURL = data.secure_url;
            }
            const combinedName = `${firstName} ${lastName}`.trim() || user?.displayName || user?.email?.split('@')[0];

            // Update Firebase Auth
            await updateProfile(user, { displayName: combinedName, photoURL: photoURL || user?.photoURL || '' });
            // Save extended fields to Firestore
            await setDoc(doc(db, 'app_users', user.uid), {
                name: combinedName,
                firstName,
                lastName,
                email: user.email,
                phone,
                jobTitle,
                photoURL: photoURL || user?.photoURL || '',
                updatedAt: new Date(),
            }, { merge: true });

            setMessage({ type: 'success', text: 'Identity updated successfully' });
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (err) {
            logger.error(err);
            setMessage({ type: 'error', text: 'Failed to update identity' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="animate-fade-in" style={{ textAlign: 'left' }}>
            {/* Header Area */}
            <div style={{ marginBottom: '32px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '8px', letterSpacing: '-0.02em', textAlign: 'left' }}>Personal Information</h2>
                <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', fontWeight: '500', textAlign: 'left' }}>Manage your public identity and platform-wide contact coordinates.</p>
            </div>

            {/* Avatar Row */}
            <div className="settings-section" style={{ borderTop: 'none', paddingTop: 0, textAlign: 'left' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                        <div style={{
                            width: '64px', height: '64px', borderRadius: '18px',
                            overflow: 'hidden', background: 'var(--bg-subtle)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', border: '1px solid var(--border)',
                            transition: 'all var(--transition-fast)'
                        }} 
                        onClick={() => fileRef.current?.click()}>
                            {avatarPreview
                                ? <img src={avatarPreview} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                : <span style={{ fontSize: '18px', fontWeight: '600', color: 'var(--accent)' }}>{initials}</span>
                            }
                        </div>
                        <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            style={{
                                position: 'absolute', bottom: '-2px', right: '-2px',
                                width: '22px', height: '22px', borderRadius: '50%',
                                background: 'var(--accent)', border: '2px solid var(--bg-surface)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', color: '#fff', boxShadow: 'var(--shadow-sm)'
                            }}
                        >
                            <ObliqueIcon name="edit" size={12} />
                        </button>
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: 'none' }} onChange={handleAvatarChange} />
                    </div>
                    <div>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>{firstName || 'User'} {lastName}</div>
                        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '2px' }}>{user?.email}</div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                            <button type="button" onClick={() => fileRef.current?.click()} style={{ fontSize: '11px', color: 'var(--accent)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0', fontWeight: '500' }}>
                                Change Image
                            </button>
                            {avatarPreview && (
                                <>
                                    <span style={{ color: 'var(--border)', fontSize: '11px' }}>•</span>
                                    <button type="button" onClick={() => { setAvatarPreview(null); setAvatarFile(null); }} style={{ fontSize: '11px', color: 'var(--danger)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0', fontWeight: '500' }}>
                                        Remove
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', marginTop: '8px' }}>
                <div className="settings-section">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                        <div style={{ textAlign: 'left' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px', textAlign: 'left' }}>First Name</label>
                            <input className="input" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="e.g. Sanjay" style={{ width: '100%', height: '42px', borderRadius: '12px', fontSize: '14px', border: '1px solid var(--border)', background: 'var(--bg-surface)' }} />
                        </div>
                        <div style={{ textAlign: 'left' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px', textAlign: 'left' }}>Last Name</label>
                            <input className="input" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="e.g. Dev" style={{ width: '100%', height: '42px', borderRadius: '12px', fontSize: '14px', border: '1px solid var(--border)', background: 'var(--bg-surface)' }} />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                        <div style={{ textAlign: 'left' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px', textAlign: 'left' }}>Identifier (Email)</label>
                            <input className="input" value={user?.email} disabled style={{ width: '100%', height: '42px', borderRadius: '12px', background: 'var(--bg-muted)', opacity: 0.6, cursor: 'not-allowed', fontSize: '14px', border: '1px solid var(--border)' }} />
                        </div>
                        <div style={{ textAlign: 'left' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px', textAlign: 'left' }}>Phone Channel</label>
                            <input className="input" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" style={{ width: '100%', height: '42px', borderRadius: '12px', fontSize: '14px', border: '1px solid var(--border)', background: 'var(--bg-surface)' }} />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                        <div style={{ textAlign: 'left' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px', textAlign: 'left' }}>Professional Title</label>
                            <input className="input" value={jobTitle} onChange={e => setJobTitle(e.target.value)} placeholder="e.g. Senior Strategist" style={{ width: '100%', height: '42px', borderRadius: '12px', fontSize: '14px', border: '1px solid var(--border)', background: 'var(--bg-surface)' }} />
                        </div>
                        <div style={{ textAlign: 'left' }}>
                            <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px', textAlign: 'left' }}>System Clearance</label>
                            <div style={{ height: '42px', display: 'flex', alignItems: 'center', gap: '8px', padding: '0 12px', background: 'var(--bg-subtle)', borderRadius: '12px', fontSize: '13px', color: 'var(--text-secondary)', border: '1px solid var(--border)', fontWeight: '500' }}>
                                <ObliqueIcon name="verified" size={14} color="var(--accent)" />
                                {userRole}
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ 
                    display: 'flex', justifyContent: 'flex-end', marginTop: '16px',
                    paddingTop: '32px', borderTop: '1px solid var(--border)' 
                }}>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                        style={{
                            minWidth: 160, height: '44px', borderRadius: '12px',
                            display: 'inline-flex', alignItems: 'center',
                            justifyContent: 'center', gap: '8px', transition: 'all var(--transition-normal)',
                            background: saved ? 'var(--success)' : 'var(--accent)',
                            fontSize: '14px', fontWeight: '500'
                        }}
                    >
                        {loading ? 'Saving...' : saved ? <><ObliqueIcon name="check" size={18} /> Profile Saved</> : 'Update Identity'}
                    </button>
                </div>
            </form>

            {/* Walkthrough Replay Section */}
            <div className="settings-section" style={{ marginTop: '24px', textAlign: 'left' }}>
                <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '4px' }}>Walkthrough</div>
                    <div style={{ fontSize: '13px', color: 'var(--text-tertiary)' }}>A 2 minute guided tour of the platform</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '24px' }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                        Replay the step by step walkthrough that runs after first login. It covers the dashboard, navigation, and core features.
                    </div>
                    <button 
                        type="button"
                        onClick={() => {
                            localStorage.setItem('oblique_force_tour', 'true');
                            window.location.href = '/';
                        }}
                        style={{
                            padding: '8px 16px', borderRadius: '8px', border: '1px solid var(--border)',
                            background: 'var(--bg-surface)', fontSize: '13px', fontWeight: '500',
                            color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0,
                            transition: 'all 0.2s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-muted)' }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-surface)' }}
                    >
                        Replay the Walkthrough
                    </button>
                </div>
            </div>
        </div>
    );
}
